import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Light / dark, decided in one place. See design/paleta.md §3.
//
// Three states and not two:
//   'system' — no stored choice; prefers-color-scheme decides. The default.
//   'light' / 'dark' — an explicit choice, which wins over the system.
//
// The CSS does the work: design/tokens.css answers the media query on its own
// and reads `data-theme` off <html>. This module only writes that attribute
// and remembers the choice. Nothing here holds a colour.

const STORAGE_KEY = 'mm-theme';
const ThemeContext = createContext(null);

function storedChoice() {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return value === 'light' || value === 'dark' ? value : 'system';
    } catch {
        // Private mode, blocked storage: the system decides and nothing breaks.
        return 'system';
    }
}

function systemPrefersDark() {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

// The attribute is REMOVED for 'system' rather than set to a value: it is the
// absence of data-theme that lets the media query in tokens.css apply.
function applyChoice(choice) {
    const root = document.documentElement;
    if (choice === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', choice);
}

export function ThemeProvider({ children }) {
    const [choice, setChoice] = useState(storedChoice);
    // What is actually on screen, which is not the same as what was chosen.
    const [systemDark, setSystemDark] = useState(systemPrefersDark);

    useEffect(() => {
        applyChoice(choice);
        try {
            if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
            else localStorage.setItem(STORAGE_KEY, choice);
        } catch {
            // Ignore: the theme still applies for this session.
        }
    }, [choice]);

    // Following the system means following it as it changes — somebody whose
    // machine flips to dark at sunset expects the panel to come along.
    useEffect(() => {
        const query = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (!query) return undefined;
        const onChange = (event) => setSystemDark(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    const resolved = choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

    // The toggle switches what is ON SCREEN, so the first click always does
    // something visible. Toggling the stored choice instead would look dead to
    // anybody sitting on 'system'.
    const toggle = useCallback(() => {
        setChoice(resolved === 'dark' ? 'light' : 'dark');
    }, [resolved]);

    const followSystem = useCallback(() => setChoice('system'), []);

    const value = useMemo(
        () => ({ choice, resolved, toggle, followSystem }),
        [choice, resolved, toggle, followSystem],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
    return ctx;
}
